<script>
import {BButton} from "buefy";
import { format } from 'date-fns';
import {getDateLocale} from "@/utils/locale.js";

export default {
    components: {BButton},
    props: ['training', 'showDescription'],

    data() {
        return {
            termsAccepted: false,
            selectedDateId: null,
            errors: {
                terms: null,
                training_date: null
            },
            loading: false,
            showFullDescription: false
        }
    },

    computed: {
        hasDates() {
            return this.training.dates && this.training.dates.length > 0;
        },
        canEnroll() {
            return !this.hasDates || this.selectedDateId !== null;
        },
        formattedDates() {
            if (!this.hasDates) return [];

            return this.training.dates.map(date => {
                const d = new Date(date.starts_at);

                return {
                    id: date.id,
                    label: format(d, "d MMMM yyyy, HH:mm", { locale: getDateLocale(this.$i18n.locale) }),
                };
            });
        },
        descriptionButtonText() {
            return this.showFullDescription ? this.$t('messages.show_less') : this.$t('messages.show_more');
        }
    },

    methods: {
        enroll() {
            this.errors = { terms: null, training_date: null };

            if (!this.termsAccepted) {
                this.errors.terms = this.$t('trainings.errors.agreement_required');
            }
            if (this.hasDates && !this.selectedDateId) {
                this.errors.training_date = this.$t('trainings.errors.select_training_date');
            }
            if (this.errors.terms || this.errors.training_date) {
                return;
            }

            this.loading = true;

            const payload = {};

            if (this.selectedDateId) {
                payload.training_date_id = this.selectedDateId;
            }

            axios.post('/trainings/' + this.training.id + '/add-user', payload).then(() => {
                this.$toast.success(this.$t('trainings.messages.enrollment_successful'));
                this.$emit('onEnroll');
                this.close();
            }).catch(error => {
                if (error.response?.data?.error) {
                    this.$toast.open({ message: error.response.data.error, type: 'error' });
                }
                if (error.response?.data?.errors) {
                    this.errors = error.response.data.errors;
                }
            }).finally(() => {
                this.loading = false;
            });
        },
        toggleFullDescription() {
            this.showFullDescription = !this.showFullDescription;
        },

        close() {
            this.$emit('onClose');
        }
    }
};
</script>

<template>
    <section>
        <div class="modal training-enroll-modal is-active">
            <div class="modal-background"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">{{ $t('trainings.labels.enrollment') }}</p>
                    <button class="delete" aria-label="close" @click="close"></button>
                </header>
                <section class="modal-card-body">
                    <div class="block">
                        <p class="title is-5">{{ training.name }}</p>
                        <p>{{ $t('trainings.fields.price.name') }}: {{ training.price }}</p>
                    </div>

                    <div class="content mt-4" v-if="showDescription" >
                        <div class="training-description mb-2"
                             :class="{ 'is-expanded': showFullDescription }">
                            {{ training.description }}
                        </div>
                        <a href="" @click.prevent="toggleFullDescription" class="is-small">
                            {{ descriptionButtonText }}
                        </a>
                    </div>

                    <div v-if="hasDates" class="field mb-4">
                        <label class="label">{{ $t('trainings.labels.select_date') }}</label>
                        <div class="buttons">
                            <b-button
                                v-for="date in formattedDates"
                                :key="date.id"
                                :type="{ 'is-success': selectedDateId === date.id }"
                                @click="selectedDateId = date.id"
                            >
                                {{ date.label }}
                            </b-button>
                        </div>
                        <p v-if="errors.training_date" class="help is-danger">{{ errors.training_date }}</p>
                    </div>

                    <div class="field">
                        <label class="checkbox">
                            <input type="checkbox" v-model="termsAccepted"/>
                            {{ $t('trainings.agreements.payment_agreement')}}
                        </label>
                        <p v-if="errors.terms" class="help is-danger">{{ errors.terms }}</p>
                    </div>
                </section>
                <footer class="modal-card-foot">
                    <div class="buttons">
                        <b-button @click="enroll()"
                                  :disabled="loading || !canEnroll"
                                  :loading="loading"
                                  type="is-success">
                            {{ $t('actions.enroll') }}
                        </b-button>
                        <b-button @click="close">{{ $t('actions.cancel') }}</b-button>
                    </div>
                </footer>
            </div>
        </div>
    </section>
</template>

<style scoped>
.training-enroll-modal .modal-card-body {
    min-height: 500px;
}
.training-description {
    white-space: pre-line;
    max-height: 3em;
    overflow: hidden;
    line-height: 1.5;

    &.is-expanded {
        max-height: none;
    }
}
</style>
